/**
 * MessageGenerator
 * Selects and generates encouragement messages based on patterns
 */

import { v4 as uuidv4 } from 'uuid';
import { EncouragementMessage, EncouragementPattern } from '../../models/EncouragementMessage';
import messageTemplates from './messageTemplates.json';

/**
 * Get a random message for a specific pattern
 */
export const getMessageForPattern = (
  pattern: EncouragementPattern
): string => {
  const templates = messageTemplates[pattern];

  if (!templates || templates.length === 0) {
    return 'Great work! Keep up the momentum.';
  }

  // Select random message from templates
  const randomIndex = Math.floor(Math.random() * templates.length);
  return templates[randomIndex];
};

/**
 * Create a new encouragement message
 */
export const createEncouragementMessage = (
  accountId: string,
  pattern: EncouragementPattern
): EncouragementMessage => {
  return {
    id: uuidv4(),
    accountId,
    pattern,
    message: getMessageForPattern(pattern),
    displayedAt: Date.now(),
    feedbackRating: null,
    feedbackAt: null,
    source: 'template',
  };
};

/**
 * Check if a pattern should be shown based on feedback history
 * Returns false if pattern has >3 thumbs down
 */
export const shouldShowPattern = (
  pattern: EncouragementPattern,
  feedbackHistory: EncouragementMessage[]
): boolean => {
  const patternFeedback = feedbackHistory.filter(m => m.pattern === pattern);

  const thumbsDownCount = patternFeedback.filter(
    m => m.feedbackRating === 'thumbs_down'
  ).length;

  return thumbsDownCount <= 3;
};

/**
 * Get the last time a pattern was shown
 */
export const getLastShownTimestamp = (
  pattern: EncouragementPattern,
  messageHistory: EncouragementMessage[]
): number => {
  const patternMessages = messageHistory
    .filter(m => m.pattern === pattern)
    .sort((a, b) => b.displayedAt - a.displayedAt);

  return patternMessages.length > 0 ? patternMessages[0].displayedAt : 0;
};

/**
 * Check if enough time has passed since last message of this pattern
 * (Prevent showing same pattern multiple times in one day)
 */
export const canShowPatternToday = (
  pattern: EncouragementPattern,
  messageHistory: EncouragementMessage[]
): boolean => {
  const lastShown = getLastShownTimestamp(pattern, messageHistory);

  if (lastShown === 0) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const lastShownDate = new Date(lastShown);
  lastShownDate.setHours(0, 0, 0, 0);

  return lastShownDate.getTime() < today.getTime();
};
